import { useEffect, useState, useMemo } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { collections, type CodexEntry } from "@/client/db";
import { apiJson } from "@/client/lib/api";
import type { ImageCategory } from "@/db/schema";

export interface ImageMetadata {
  id: string;
  storyId: string;
  category: ImageCategory;
  name: string;
  codexId: string | null;
  isDefault: boolean;
  order: number;
  /** In bytes */
  size: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ImageWithCodexInfo extends ImageMetadata {
  codexName: string | null;
  codexType: string | null;
}

// Hook to get all images for a story with codex info
export function useAllImages(storyId: string) {
  const [images, setImages] = useState<ImageMetadata[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { data: codexData } = useLiveQuery(() => collections().codex.collection, [storyId]);

  // Fetch images from API
  useEffect(() => {
    if (!storyId) return;

    const fetchImages = async () => {
      setIsLoading(true);
      setError(null);
      try {
        setImages(await apiJson<ImageMetadata[]>(`/api/images/story/${storyId}`));
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchImages();
  }, [storyId]);

  // Build a map of codexId -> codex entry info
  const codexMap = useMemo(() => {
    const map = new Map<string, { name: string; type: string }>();
    const entries = (codexData ?? []).filter(
      (entry: CodexEntry) => entry.storyId === storyId
    );
    entries.forEach((entry: CodexEntry) => {
      map.set(entry.id, { name: entry.name, type: entry.type });
    });
    return map;
  }, [codexData, storyId]);

  // Enrich images with codex info
  const imagesWithCodexInfo: ImageWithCodexInfo[] = useMemo(() => {
    return images.map((img) => {
      const codexInfo = img.codexId ? codexMap.get(img.codexId) : null;
      return {
        ...img,
        codexName: codexInfo?.name ?? null,
        codexType: codexInfo?.type ?? null,
      };
    });
  }, [images, codexMap]);

  // Group by category
  const imagesByCategory = useMemo(() => {
    const groups: Record<ImageCategory, ImageWithCodexInfo[]> = {
      portrait: [],
      background: [],
      sprite: [],
      sheet: [],
      expression: [],
      outfit: [],
      cg: [],
      reference: [],
    };
    imagesWithCodexInfo.forEach((img) => {
      groups[img.category].push(img);
    });
    return groups;
  }, [imagesWithCodexInfo]);

  return {
    images: imagesWithCodexInfo,
    imagesByCategory,
    isLoading,
    error,
    refetch: () => {
      // Trigger a refetch by updating storyId dependency
      const fetchImages = async () => {
        setIsLoading(true);
        try {
          setImages(await apiJson<ImageMetadata[]>(`/api/images/story/${storyId}`));
        } catch (err) {
          setError(err instanceof Error ? err.message : "Unknown error");
        } finally {
          setIsLoading(false);
        }
      };
      fetchImages();
    },
  };
}
